import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { alertAPI, entityAPI, transactionAPI, caseAPI } from '../lib/api';
import { formatDate, formatRelativeTime, formatCurrency, getRiskClass } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  AlertTriangle,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  FileText,
  User,
  Activity,
  ExternalLink,
  Eye,
  Filter,
  Send,
  Folder,
  ArrowUpCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const ALERT_STATUSES = [
  { value: 'new', label: 'New', icon: AlertTriangle, color: 'text-cyan-400 border-cyan-400 bg-cyan-400/10' },
  { value: 'investigating', label: 'Investigating', icon: Clock, color: 'text-orange-400 border-orange-400 bg-orange-400/10' },
  { value: 'escalated', label: 'Escalated', icon: ArrowUpCircle, color: 'text-red-400 border-red-400 bg-red-400/10' },
  { value: 'resolved', label: 'Resolved', icon: CheckCircle, color: 'text-green-400 border-green-400 bg-green-400/10' },
  { value: 'false_positive', label: 'False Positive', icon: XCircle, color: 'text-gray-400 border-gray-400 bg-gray-400/10' },
];

const SEVERITIES = ['critical', 'high', 'medium', 'low'];

// Alert List Component
function AlertList() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', severity: '' });
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchAlerts();
  }, [page, filters.status, filters.severity]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
        ...(filters.status && { status: filters.status }),
        ...(filters.severity && { severity: filters.severity }),
      };
      const response = await alertAPI.list(params);
      setAlerts(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (alertId, newStatus, e) => {
    e?.stopPropagation();
    try {
      await alertAPI.update(alertId, { status: newStatus });
      toast.success('Alert status updated');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusConfig = (status) => ALERT_STATUSES.find(s => s.value === status) || ALERT_STATUSES[0];

  if (loading && alerts.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Alert Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total.toLocaleString()} alerts in queue
          </p>
        </div>
        <Button variant="outline" onClick={fetchAlerts} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Status</SelectItem>
                {ALERT_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.severity || "all"} onValueChange={(v) => setFilters({...filters, severity: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10">
                <Shield className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Severities</SelectItem>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="ghost" onClick={() => setFilters({ status: '', severity: '' })}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alerts Table */}
      <Card className="glass">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[4px]"></TableHead>
                <TableHead>Alert</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((alert) => {
                const statusConfig = getStatusConfig(alert.status);
                const StatusIcon = statusConfig.icon;
                return (
                  <TableRow 
                    key={alert.id} 
                    className="cursor-pointer hover:bg-white/5"
                    onClick={() => navigate(`/alerts/${alert.id}`)}
                  >
                    <TableCell className="p-0">
                      <div className={`alert-indicator h-full min-h-[60px] ${alert.severity}`} />
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-white">{alert.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{alert.description}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs uppercase">{alert.alert_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${getRiskClass(alert.severity)} text-xs uppercase`}>
                        {alert.severity}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={alert.status}
                        onValueChange={(value) => handleStatusChange(alert.id, value)}
                      >
                        <SelectTrigger 
                          className={`w-[140px] h-8 ${statusConfig.color} border`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <StatusIcon className="w-3 h-3 mr-2" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {ALERT_STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelativeTime(alert.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/alerts/${alert.id}`); }}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Alert Detail Component
function AlertDetail({ alertId }) {
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [linkedEntities, setLinkedEntities] = useState([]);
  const [linkedTransactions, setLinkedTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalateNote, setEscalateNote] = useState('');

  useEffect(() => {
    fetchAlertDetails();
  }, [alertId]);

  const fetchAlertDetails = async () => {
    setLoading(true);
    try {
      const alertRes = await alertAPI.get(alertId);
      setAlert(alertRes.data);

      // Fetch linked entities
      if (alertRes.data.entity_ids?.length > 0) {
        const entitiesRes = await entityAPI.list({ limit: 100 });
        const linked = entitiesRes.data.items.filter(e => 
          alertRes.data.entity_ids.includes(e.id)
        );
        setLinkedEntities(linked);
      }

      // Fetch linked transactions
      if (alertRes.data.transaction_ids?.length > 0) {
        const txnRes = await transactionAPI.list({ limit: 100 });
        const linked = txnRes.data.items.filter(t => 
          alertRes.data.transaction_ids.includes(t.id)
        );
        setLinkedTransactions(linked);
      }
    } catch (error) {
      toast.error('Failed to load alert details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await alertAPI.update(alertId, { status: newStatus });
      toast.success('Alert status updated');
      fetchAlertDetails();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEscalateToCase = async () => {
    try {
      const caseData = {
        title: `Investigation: ${alert.title}`,
        description: `${alert.description}\n\nEscalation Note: ${escalateNote}`,
        priority: alert.severity === 'critical' ? 'critical' : alert.severity === 'high' ? 'high' : 'medium',
        alert_ids: [alertId],
        entity_ids: alert.entity_ids || [],
      };
      
      await caseAPI.create(caseData);
      await alertAPI.update(alertId, { status: 'escalated' });
      
      toast.success('Alert escalated to case');
      setShowEscalateModal(false);
      navigate('/cases');
    } catch (error) {
      toast.error('Failed to escalate alert');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Alert not found</p>
        <Button variant="outline" onClick={() => navigate('/alerts')} className="mt-4">
          Back to Alerts
        </Button>
      </div>
    );
  }

  const statusConfig = ALERT_STATUSES.find(s => s.value === alert.status) || ALERT_STATUSES[0];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alert-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/alerts')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-2">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              alert.severity === 'critical' ? 'bg-red-500/20' : 
              alert.severity === 'high' ? 'bg-orange-500/20' : 'bg-yellow-500/20'
            }`}>
              <AlertTriangle className={`w-6 h-6 ${
                alert.severity === 'critical' ? 'text-red-400' : 
                alert.severity === 'high' ? 'text-orange-400' : 'text-yellow-400'
              }`} />
            </div>
            <div>
              <h1 className="font-secondary text-xl font-bold text-white">
                {alert.title}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <Badge variant="outline" className="text-xs uppercase">
                  {alert.alert_type}
                </Badge>
                <Badge variant="outline" className={`${getRiskClass(alert.severity)} text-xs uppercase`}>
                  {alert.severity}
                </Badge>
                <Badge variant="outline" className={`${statusConfig.color} text-xs uppercase`}>
                  {alert.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Select value={alert.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Update Status" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {ALERT_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={showEscalateModal} onOpenChange={setShowEscalateModal}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="escalate-btn">
                <Folder className="w-4 h-4" />
                Escalate to Case
              </Button>
            </DialogTrigger>
            <DialogContent className="glass border-border">
              <DialogHeader>
                <DialogTitle>Escalate Alert to Case</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Escalation Note</Label>
                  <Textarea
                    value={escalateNote}
                    onChange={(e) => setEscalateNote(e.target.value)}
                    placeholder="Add notes about why this alert is being escalated..."
                    className="bg-black/20 border-white/10 min-h-[100px]"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="ghost" onClick={() => setShowEscalateModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleEscalateToCase}>
                    Create Case
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Description */}
      <Card className="glass">
        <CardContent className="p-4">
          <p className="text-muted-foreground">{alert.description}</p>
        </CardContent>
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Alert Details */}
        <Card className="glass">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-secondary text-sm uppercase tracking-wider">
              Alert Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="label-mono">Created</p>
                <p className="text-white">{formatDate(alert.created_at)}</p>
              </div>
              <div>
                <p className="label-mono">Last Updated</p>
                <p className="text-white">{formatRelativeTime(alert.updated_at)}</p>
              </div>
              <div>
                <p className="label-mono">Alert Type</p>
                <p className="text-white capitalize">{alert.alert_type?.replace(/_/g, ' ')}</p>
              </div>
              <div>
                <p className="label-mono">Assigned To</p>
                <p className="text-white">{alert.assigned_to || 'Unassigned'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Linked Entities */}
        <Card className="glass">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-secondary text-sm uppercase tracking-wider flex items-center gap-2">
              <User className="w-4 h-4" />
              Linked Entities ({linkedEntities.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {linkedEntities.length > 0 ? (
              <div className="space-y-2">
                {linkedEntities.map((entity) => (
                  <Link 
                    key={entity.id} 
                    to={`/entities/${entity.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div>
                      <p className="text-white font-medium">{entity.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{entity.entity_type}</p>
                    </div>
                    <Badge variant="outline" className={`${getRiskClass(entity.risk_level)} text-xs`}>
                      {entity.risk_level}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <User className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">No linked entities</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Linked Transactions */}
      {linkedTransactions.length > 0 && (
        <Card className="glass">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-secondary text-sm uppercase tracking-wider flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Linked Transactions ({linkedTransactions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linkedTransactions.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {txn.is_flagged && <AlertTriangle className="w-4 h-4 text-red-400" />}
                        <span className="font-mono text-xs">{txn.transaction_ref}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs uppercase">{txn.transaction_type}</Badge>
                    </TableCell>
                    <TableCell className="font-mono">
                      {formatCurrency(txn.amount, txn.currency)}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs">{txn.risk_score || 0}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelativeTime(txn.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Link to={`/transactions/${txn.id}`}>
                        <Button variant="ghost" size="icon">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="glass">
        <CardHeader className="border-b border-white/5 pb-4">
          <CardTitle className="font-secondary text-sm uppercase tracking-wider">
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="gap-2" onClick={() => handleStatusChange('investigating')}>
              <Clock className="w-4 h-4" />
              Mark Investigating
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => handleStatusChange('resolved')}>
              <CheckCircle className="w-4 h-4" />
              Resolve
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => handleStatusChange('false_positive')}>
              <XCircle className="w-4 h-4" />
              False Positive
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Component
export default function Alerts() {
  const { id } = useParams();
  
  if (id) {
    return <AlertDetail alertId={id} />;
  }
  
  return <AlertList />;
}
